import React, { useState, useEffect } from 'react';
import { Navbar } from './Navbar';
import { ChevronLeft, ChevronDown, Lock } from 'lucide-react';

const THEMES = [
    { id: 'work', label: 'Professional Synergy', short: 'Work', icon: '💼', desc: 'Workplace & Career' },
    { id: 'friendship', label: 'Friendship Resonance', short: 'Friendship', icon: '🤝', desc: 'Social Vibes & Loyalties' },
    { id: 'relationship', label: 'Romantic Harmony', short: 'Romantic', icon: '❤️', desc: 'Love & Intimacy' },
    { id: 'cofounder', label: 'Co-Founder Alignment', short: 'Startup', icon: '🚀', desc: 'Startup & Execution' }
];

const Accordion = ({ title, icon, items, color }) => {
    const [isOpen, setIsOpen] = useState(false);
    if (!items || items.length === 0) return null;
    return (
        <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '16px', border: `1px solid ${color}40`, marginBottom: '12px', overflow: 'hidden', transition: 'all 0.3s' }}>
            <button 
                onClick={() => setIsOpen(!isOpen)} 
                style={{ width: '100%', padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'transparent', border: 'none', cursor: 'pointer', color: 'white', fontWeight: 'bold' }}
            >
                <span style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '14px' }}>
                    <span style={{ fontSize: '18px' }}>{icon}</span> {title}
                </span>
                <ChevronDown size={18} style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', color: 'rgba(255,255,255,0.5)' }} />
            </button>
            <div style={{
                maxHeight: isOpen ? '1000px' : '0', opacity: isOpen ? 1 : 0,
                transition: 'all 0.3s ease-in-out', overflow: 'hidden'
            }}>
                <div style={{ padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {items.map((item, i) => (
                        <div key={i}>
                            {item.title && <div style={{ fontSize: '13px', fontWeight: '700', color: color, marginBottom: '4px' }}>{item.title}</div>}
                            <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', lineHeight: '1.6' }}>{item.description}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const SynergyCard = ({ theme, report, isLoading, onAnalyze, hasPartnerProfile }) => {
    if (isLoading) {
        return (
            <div className="glass-panel" style={{ width: '100%', padding: '60px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', animation: 'fadeIn 0.3s ease' }}>
                <div className="loading-spinner" style={{ margin: '0 auto 20px', width: '50px', height: '50px', borderTopColor: '#ec4899' }}></div>
                <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px', color: 'white' }}>Analyzing Synergy...</h3>
                <p style={{ fontSize: '13px', color: '#ec4899' }}>Running AI Analysis for {theme.label}</p>
            </div>
        );
    }

    if (!report) {
        if (!hasPartnerProfile) {
            return (
                <div className="glass-panel" style={{ width: '100%', padding: '60px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', animation: 'fadeIn 0.3s ease' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
                    <h3 style={{ fontSize: '20px', fontWeight: '800', marginBottom: '8px', color: 'white' }}>Waiting on their persona</h3>
                    <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', lineHeight: '1.6', maxWidth: '280px' }}>
                        Your connection hasn't completed their personality profile yet. Once they do, synergy analysis unlocks automatically.
                    </p>
                </div>
            );
        }
        return (
            <div className="glass-panel" style={{ width: '100%', padding: '60px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', animation: 'fadeIn 0.3s ease' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px', filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.2))' }}>{theme.icon}</div>
                <h3 style={{ fontSize: '20px', fontWeight: '800', marginBottom: '8px', color: 'white' }}>{theme.label}</h3>
                <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', marginBottom: '32px', lineHeight: '1.5' }}>{theme.desc}</p>
                <button
                    onClick={onAnalyze}
                    style={{
                        width: '100%', maxWidth: '300px', padding: '16px', borderRadius: '16px',
                        background: 'linear-gradient(135deg, #ec4899, #8b5cf6)', color: 'white', border: 'none',
                        fontSize: '15px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 8px 20px rgba(236,72,153,0.3)',
                        transition: 'transform 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                    }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.02)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                >
                    <Lock size={18} />
                    Reveal Synergy
                </button>
            </div>
        );
    }

    const strengths = report.key_dynamics?.filter(d => d.type === 'strength') || [];
    const weaknesses = report.key_dynamics?.filter(d => d.type !== 'strength') || [];

    return (
        <div className="glass-panel invite-stagger-1" style={{ width: '100%', padding: '32px 24px', display: 'flex', flexDirection: 'column', animation: 'fadeIn 0.3s ease' }}>

            {/* Score + Headline */}
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>{theme.icon}</div>
                <div style={{
                    width: '80px', height: '80px', borderRadius: '50%', border: '4px solid #ec4899',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '28px', fontWeight: '900', margin: '0 auto 16px',
                    color: '#ec4899', background: 'rgba(236,72,153,0.1)',
                    boxShadow: '0 0 30px rgba(236, 72, 153, 0.2)'
                }}>
                    {report.compatibility_score}%
                </div>
                <h2 style={{ fontSize: '22px', fontWeight: '800', margin: '0 0 8px', color: 'white' }}>{report.headline}</h2>
                <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', lineHeight: '1.6' }}>{report.summary}</p>
            </div>

            {/* Compatibility Story — the shareable moment */}
            {report.compatibilityStory && (
                <div style={{
                    background: 'linear-gradient(135deg, rgba(236,72,153,0.1), rgba(139,92,246,0.1))',
                    border: '1px solid rgba(236,72,153,0.3)',
                    borderRadius: '16px', padding: '20px', marginBottom: '16px',
                    textAlign: 'center'
                }}>
                    <p style={{ margin: '0 0 6px', fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700 }}>
                        ✦ Your Dynamic
                    </p>
                    <p style={{ margin: 0, fontSize: '1rem', color: 'white', fontWeight: 600, lineHeight: 1.55, fontStyle: 'italic' }}>
                        "{report.compatibilityStory}"
                    </p>
                </div>
            )}

            {/* Famous Duo */}
            {report.famousDuo && (
                <div style={{
                    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '14px', padding: '16px', marginBottom: '16px',
                    display: 'flex', gap: '12px', alignItems: 'flex-start'
                }}>
                    <span style={{ fontSize: '24px', flexShrink: 0 }}>🎭</span>
                    <div>
                        <p style={{ margin: '0 0 4px', fontSize: '0.75rem', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
                            Famous Duo Like You
                        </p>
                        <p style={{ margin: '0 0 6px', fontSize: '0.9rem', color: '#c084fc', fontWeight: 800 }}>
                            {report.famousDuo.names}
                        </p>
                        <p style={{ margin: 0, fontSize: '0.82rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.55 }}>
                            {report.famousDuo.why}
                        </p>
                    </div>
                </div>
            )}

            {/* Dynamics accordions */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Accordion title="What Works Well" icon="✨" items={strengths} color="#34d399" />
                <Accordion title="Potential Frictions" icon="⚡" items={weaknesses} color="#f472b6" />
                <Accordion title="Actionable Advice" icon="💡" items={[{ description: report.thematic_advice }]} color="#ec4899" />
            </div>
        </div>
    );
};

export const ComparativeAnalysisPage = ({ user, connection, analysisResult, onBack, onSignInClick, initialThemeId, autoStart = false }) => {
    const [reports, setReports] = useState({});
    const [loadingThemes, setLoadingThemes] = useState({});
    const [activeIndex, setActiveIndex] = useState(() => {
        if (!initialThemeId) return 0;
        const idx = THEMES.findIndex(t => t.id === initialThemeId);
        return idx !== -1 ? idx : 0;
    });

    // Touch handlers for swiping
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const minSwipeDistance = 50;

    const [showToast, setShowToast] = useState(false);
    const [connectedName, setConnectedName] = useState('');
    const [analyzeError, setAnalyzeError] = useState('');

    useEffect(() => {
        if (sessionStorage.getItem('show_link_toast') === 'true') {
            const cName = sessionStorage.getItem('connected_user_name') || 'your friend';
            setConnectedName(cName);
            setShowToast(true);
            sessionStorage.removeItem('show_link_toast');
            setTimeout(() => setShowToast(false), 5000);
        }
    }, []);

    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEndHandler = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe && activeIndex < THEMES.length - 1) {
            setActiveIndex(prev => prev + 1);
        }
        if (isRightSwipe && activeIndex > 0) {
            setActiveIndex(prev => prev - 1);
        }
    };

    // Helpers
    const getDisplayName = (inv) => {
        if (!inv) return 'Unknown Link';
        if (inv._connected_name) return inv._connected_name;
        if (inv.invited_email && !['link-invite', 'link-invite-accepted'].includes(inv.invited_email)) {
            return inv.invited_email;
        }
        return inv._perspective === 'received' ? 'Synced Friend' : 'Linked Profile';
    };

    const name = getDisplayName(connection);

    const getProfile2Results = () => {
        const raw = connection._perspective === 'received' ? connection.inviter_test_results : connection.invited_test_results;
        let res = null;
        try { res = typeof raw === 'string' ? JSON.parse(raw) : raw; } catch {}
        return res;
    };

    const profile2 = getProfile2Results();
    const user2_id = connection._perspective === 'received' ? connection.inviter_user_id : connection.invited_user_id;

    useEffect(() => {
        const fetchReports = async () => {
            let reportMap = {};
            try {
                const t = new Date().getTime();
                const response = await fetch(`/api/compatibility/user/${user.id}?t=${t}`, { cache: 'no-store' });
                const data = await response.json();
                if (data.success && data.reports) {
                    const connReports = data.reports.filter(r => String(r.connection_id) === String(connection.id));
                    connReports.forEach(r => {
                        if (r.theme && r.report_json) {
                            reportMap[r.theme] = r.report_json;
                        }
                    });
                    setReports(reportMap);
                }
            } catch (err) {
                console.error('Failed to fetch existing reports:', err);
            }
            // Auto-trigger the active theme if arriving from synergy preview and no cached report.
            if (autoStart) {
                const activeThemeId = THEMES[activeIndex]?.id;
                if (activeThemeId && !reportMap[activeThemeId]) {
                    // Only fire if partner has profile data — otherwise the "waiting" card shows
                    const partnerResults = connection._perspective === 'received'
                        ? connection.inviter_test_results
                        : connection.invited_test_results;
                    const hasPartner = !!(partnerResults && (typeof partnerResults === 'object'
                        ? Object.keys(partnerResults).length > 0
                        : partnerResults));
                    if (hasPartner) {
                        setTimeout(() => handleAnalyzeCompatibility(activeThemeId), 0);
                    }
                }
            }
        };
        if (user?.id && connection?.id) {
            fetchReports();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, connection?.id]);

    const handleAnalyzeCompatibility = async (themeId) => {
        setAnalyzeError('');
        setLoadingThemes(prev => ({ ...prev, [themeId]: true }));
        try {
            const response = await fetch('/api/compatibility/analyze', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    connection_id: connection.id,
                    user1_id: user.id,
                    user2_id: user2_id,
                    theme: themeId,
                    profile1: analysisResult || {},
                    profile2: profile2,
                    provider: 'gemini'
                })
            });
            const data = await response.json();
            if (data.success && data.report) {
                setReports(prev => ({ ...prev, [themeId]: data.report }));
            } else {
                setAnalyzeError(data.error || 'Failed to analyze compatibility');
            }
        } catch (err) {
            console.error(err);
            setAnalyzeError('Error connecting to analysis engine. Please try again.');
        } finally {
            setLoadingThemes(prev => ({ ...prev, [themeId]: false }));
        }
    };

    const activeTheme = THEMES[activeIndex];

    return (
        <div className="invite-acceptance-page base-background" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            {showToast && (
                <div className="connection-toast" style={{
                    position: 'fixed', top: '80px', left: '50%', transform: 'translateX(-50%)',
                    background: 'rgba(16, 185, 129, 0.95)', backdropFilter: 'blur(10px)',
                    color: 'white', padding: '16px 24px', borderRadius: '50px', 
                    fontSize: '15px', fontWeight: '600',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255,255,255,0.1)', 
                    zIndex: 9999, display: 'flex', alignItems: 'center', gap: '10px',
                    animation: 'toastSlideDown 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards',
                    whiteSpace: 'nowrap'
                }}>
                    <style>{`
                        @keyframes toastSlideDown { 
                            from { opacity: 0; transform: translate(-50%, -20px) scale(0.95); } 
                            to { opacity: 1; transform: translate(-50%, 0) scale(1); } 
                        }
                    `}</style>
                    <span style={{ fontSize: '18px' }}>✨</span>
                    <span>Connected with <strong>{connectedName}</strong></span>
                </div>
            )}
            
            <Navbar currentUser={user} onSignInClick={onSignInClick} />
            
            <div className="ambient-bg">
                <div className="ambient-orb orb-purple"></div>
                <div className="ambient-orb orb-pink"></div>
            </div>

            <main style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 1, paddingBottom: '40px', paddingTop: '64px' }}>
                <div style={{ padding: '16px', maxWidth: '600px', margin: '0 auto', width: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, justifyContent: 'center' }}>
                    <button 
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onBack();
                        }}
                        style={{
                            position: 'relative', zIndex: 10,
                            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '12px', padding: '10px 16px', color: 'rgba(255,255,255,0.8)',
                            display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer',
                            fontSize: '14px', fontWeight: '600', marginBottom: '16px',
                            transition: 'all 0.2s', width: 'fit-content'
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                    >
                        <ChevronLeft size={18} />
                        Back to Connections
                    </button>

                    <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                        <h1 style={{ fontSize: '28px', fontWeight: '800', marginBottom: '8px', background: 'linear-gradient(135deg, #ec4899, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                            Synergy Analysis
                        </h1>
                        <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.6)', margin: 0 }}>
                            You ↔ <strong style={{ color: 'white' }}>{name}</strong>
                        </p>
                    </div>

                    {/* Navigation Tabs */}
                    <div style={{ 
                        width: '100%', display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '8px', paddingBottom: '8px', marginBottom: '24px',
                        scrollbarWidth: 'none', msOverflowStyle: 'none'
                    }}>
                        <style>{`div::-webkit-scrollbar { display: none; }`}</style>
                        {THEMES.map((theme, index) => (
                            <button
                                key={theme.id}
                                onClick={() => setActiveIndex(index)}
                                style={{
                                    whiteSpace: 'nowrap', padding: '10px 16px', borderRadius: '24px',
                                    background: activeIndex === index ? 'rgba(236,72,153,0.15)' : 'rgba(255,255,255,0.05)',
                                    color: activeIndex === index ? '#ec4899' : 'rgba(255,255,255,0.6)',
                                    border: `1px solid ${activeIndex === index ? 'rgba(236,72,153,0.3)' : 'rgba(255,255,255,0.1)'}`,
                                    cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '6px',
                                    fontWeight: activeIndex === index ? '700' : '600', fontSize: '13px'
                                }}
                            >
                                <span>{theme.icon}</span> {theme.short}
                                {reports[theme.id] && <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#34d399', marginLeft: '4px' }}></span>}
                            </button>
                        ))}
                    </div>

                    {/* Inline error banner */}
                    {analyzeError && (
                        <div style={{
                            width: '100%', padding: '14px 18px', borderRadius: '14px', marginBottom: '16px',
                            background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)',
                            color: 'rgba(255,255,255,0.85)', fontSize: '13px', lineHeight: '1.5',
                            display: 'flex', alignItems: 'flex-start', gap: '10px'
                        }}>
                            <span style={{ flexShrink: 0, fontSize: '16px' }}>⚠️</span>
                            <span>{analyzeError}</span>
                            <button
                                onClick={() => setAnalyzeError('')}
                                style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: '18px', lineHeight: 1, flexShrink: 0 }}
                            >×</button>
                        </div>
                    )}

                    {/* Main Stage (Swipeable Component) */}
                    <div
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEndHandler}
                        style={{ flex: 1, position: 'relative', width: '100%', overflow: 'hidden' }}
                    >
                        <SynergyCard
                            theme={activeTheme}
                            report={reports[activeTheme.id]}
                            isLoading={loadingThemes[activeTheme.id]}
                            onAnalyze={() => handleAnalyzeCompatibility(activeTheme.id)}
                            hasPartnerProfile={!!profile2}
                        />
                    </div>
                    
                    {/* Dots indicator for swipe context */}
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '24px' }}>
                        {THEMES.map((_, i) => (
                            <div 
                                key={i} 
                                style={{
                                    width: activeIndex === i ? '24px' : '8px', 
                                    height: '8px', borderRadius: '4px',
                                    background: activeIndex === i ? '#ec4899' : 'rgba(255,255,255,0.2)',
                                    transition: 'all 0.3s'
                                }}
                            />
                        ))}
                    </div>

                </div>
            </main>
        </div>
    );
};
